import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useSEO } from "@/lib/seo";
import { api, errorMessage } from "@/lib/api";
import { COUNTRIES, COUNTRIES_FR } from "@/lib/format";
import { Field, Input, Select, Textarea, useForm } from "@/components/site/Fields";
import { Reveal } from "@/components/site/Motion";

export default function Sourcing() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [v, , bind] = useForm({
    make: params.get("make") || "", model: params.get("model") || "", version: "", production_year: "", max_mileage: "",
    exterior_color: "", interior_color: "", options: "", country_preference: "", drive: "", additional: "",
    budget_min: "", budget_max: "", timeframe: "", name: "", email: "", phone: "", whatsapp: "", country: "",
  });
  useSEO({
    title: lang === "fr" ? "Recherche personnalisée de véhicule d'exception — Palissiere Motor Group" : "Personalized exceptional vehicle sourcing — Palissiere Motor Group",
    description: lang === "fr" ? "Sourcing automobile sur mesure : Porsche, Ferrari, supercars et voitures de collection recherchées selon vos critères, en Europe et à l'international." : "Bespoke automotive sourcing: Porsche, Ferrari, supercars and collector cars found to your exact criteria across Europe and beyond.",
    lang,
  });
  const steps = [t("so_step1"), t("so_step2"), t("so_step3"), t("so_step4")];
  const valid = [() => v.make.trim().length > 0, () => true, () => true, () => v.name.trim().length > 1 && /\S+@\S+\.\S+/.test(v.email)];
  const next = () => (valid[step]() ? setStep((s) => s + 1) : toast.error(t("f_required")));
  const submit = async (e) => {
    e.preventDefault();
    if (!valid[3]()) return toast.error(t("f_required"));
    setBusy(true);
    try {
      const { name, email, phone, whatsapp, country, ...data } = v;
      await api.post("/leads/sourcing", { name, email, phone, whatsapp, country, vehicle: [v.make, v.model, v.version].filter(Boolean).join(" "), message: v.additional, data, lang });
      setDone(true);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const countryOpts = COUNTRIES.map((c) => <option key={c} value={c}>{lang === "fr" ? COUNTRIES_FR[c] : c}</option>);

  return (
    <div className="bg-[#070809] pt-28 md:pt-36">
      <div className="container-x grid gap-14 pb-24 lg:grid-cols-12 md:pb-32">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow mb-5">{t("sourcing_eyebrow")}</p>
          <h1 className="display text-4xl sm:text-5xl" data-testid="sourcing-title">{t("sourcing_title")}</h1>
          <p className="mt-6 text-base leading-relaxed text-[#c7ccd4]">{t("sourcing_copy")}</p>
          <ol className="mt-12 hidden lg:block">
            {steps.map((s, i) => (
              <li key={s} className={`mono flex items-center gap-4 border-t border-white/10 py-4 text-[0.62rem] ${i === step ? "text-white" : "text-[#6f7986]"}`} data-testid={`sourcing-step-indicator-${i + 1}`}>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-[0.55rem] ${i < step ? "border-[#e2c89d] bg-[#e2c89d] text-black" : i === step ? "border-white" : "border-white/20"}`}>{i < step ? <Check size={12} /> : i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </Reveal>
        <div className="lg:col-span-7 lg:col-start-6">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 p-10" data-testid="sourcing-success">
              <Check className="text-[#e2c89d]" />
              <h2 className="display-tight mt-6 text-2xl">{t("so_done_title")}</h2>
              <p className="mt-3 text-[#c7ccd4]">{t("so_done_copy")}</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="sourcing-form">
              <div className="mono mb-8 flex items-center justify-between text-[0.6rem] text-[#8b95a5]">
                <span data-testid="sourcing-step-label">{`0${step + 1} / 04 — ${steps[step]}`}</span>
                <span className="h-px flex-1 mx-6 bg-white/10"><span className="block h-px bg-[#e2c89d] transition-[width] duration-500" style={{ width: `${((step + 1) / 4) * 100}%` }} /></span>
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4 }} className="grid gap-7 sm:grid-cols-2">
                  {step === 0 && <>
                    <Field label={t("s_make")} required><Input data-testid="sourcing-make" {...bind("make")} /></Field>
                    <Field label={t("s_model")}><Input data-testid="sourcing-model" {...bind("model")} /></Field>
                    <Field label={t("s_version")}><Input data-testid="sourcing-version" {...bind("version")} /></Field>
                    <Field label={t("so_prod_year")}><Input data-testid="sourcing-year" {...bind("production_year")} placeholder="2019 – 2023" /></Field>
                    <Field label={t("so_max_km")}><Input data-testid="sourcing-mileage" type="number" {...bind("max_mileage")} /></Field>
                  </>}
                  {step === 1 && <>
                    <Field label={t("s_ext")}><Input data-testid="sourcing-ext-color" {...bind("exterior_color")} /></Field>
                    <Field label={t("s_int")}><Input data-testid="sourcing-int-color" {...bind("interior_color")} /></Field>
                    <Field label={t("so_options")} className="sm:col-span-2"><Input data-testid="sourcing-options" {...bind("options")} /></Field>
                    <Field label={t("so_country_pref")}><Select data-testid="sourcing-country-pref" {...bind("country_preference")}><option value="">{t("so_any")}</option>{countryOpts}</Select></Field>
                    <Field label={t("so_drive")}><Select data-testid="sourcing-drive" {...bind("drive")}><option value="">{t("so_any")}</option><option value="LHD">{t("so_lhd")}</option><option value="RHD">{t("so_rhd")}</option></Select></Field>
                    <Field label={t("so_additional")} className="sm:col-span-2"><Textarea data-testid="sourcing-additional" rows={3} {...bind("additional")} /></Field>
                  </>}
                  {step === 2 && <>
                    <Field label={`${t("so_budget_min")} (€)`}><Input data-testid="sourcing-budget-min" type="number" step="1000" {...bind("budget_min")} /></Field>
                    <Field label={`${t("so_budget_max")} (€)`}><Input data-testid="sourcing-budget-max" type="number" step="1000" {...bind("budget_max")} /></Field>
                    <Field label={t("so_timeframe")} className="sm:col-span-2">
                      <Select data-testid="sourcing-timeframe" {...bind("timeframe")}>
                        <option value="">—</option>
                        {["asap", "3", "6", "12", "flex"].map((k) => <option key={k} value={k}>{t(`so_tf_${k}`)}</option>)}
                      </Select>
                    </Field>
                  </>}
                  {step === 3 && <>
                    <Field label={t("so_full_name")} required><Input data-testid="sourcing-name" required {...bind("name")} /></Field>
                    <Field label={t("f_email")} required><Input data-testid="sourcing-email" type="email" required {...bind("email")} /></Field>
                    <Field label={t("f_phone")}><Input data-testid="sourcing-phone" type="tel" {...bind("phone")} /></Field>
                    <Field label={t("f_whatsapp")}><Input data-testid="sourcing-whatsapp" type="tel" {...bind("whatsapp")} /></Field>
                    <Field label={t("f_country")} className="sm:col-span-2"><Input data-testid="sourcing-country" {...bind("country")} /></Field>
                  </>}
                </motion.div>
              </AnimatePresence>
              <div className="mt-12 flex items-center justify-between">
                <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="sourcing-prev" className={`mono text-[0.62rem] text-[#c7ccd4] ${step === 0 ? "invisible" : ""}`}>← {t("so_prev")}</button>
                {step < 3 ? (
                  <button type="button" onClick={next} data-testid="sourcing-next" className="btn btn-ghost">{t("so_next")}</button>
                ) : (
                  <button type="submit" disabled={busy} data-testid="sourcing-submit" className="btn btn-primary disabled:opacity-60">{t("so_submit")}</button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
